import { useTranslate } from '@tolgee/react';
import { EmptyState } from '~/components/empty/EmptyState';
import { Button } from '~/components/ui/Button';

interface BlogPost {
  readonly title: string;
  readonly link: string;
}

interface BlogPosts {
  readonly blogPosts: BlogPost[];
  readonly title?: string;
  readonly cta: {
    readonly buttonText?: string;
    readonly link: string;
    readonly isPrimary?: boolean;
  };
}

export function BlogWidget(props: BlogPosts) {
  const { t } = useTranslate();
  if (props.blogPosts?.length === 0) {
    return (
      <div className="h-full flex flex-col gap-y-6">
        <div>
          <EmptyState
            title={t(props.title || 'widget_blog_title')}
            description={t('empty_no_blog_posts')}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col gap-y-6">
      <h2 className="text-lg font-bold">
        {t(props.title || 'widget_blog_title')}
      </h2>
      <ul className="divide-y divide-gray-200 dark:divide-gray-700 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {Boolean(props.blogPosts?.length) &&
          props.blogPosts.map((blog, index) => (
            <li
              key={index}
              className="flex justify-between items-center p-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors bg-white dark:bg-gray-900"
            >
              <a
                className="flex-grow text-sm font-medium text-gray-900 dark:text-gray-100 no-underline"
                href={blog?.link}
                target="_blank"
                rel="noreferrer"
                data-track="immodossier valiant blog post link"
              >
                {blog?.title}
              </a>
              <span className="ml-2 text-gray-400 dark:text-gray-500 flex-shrink-0">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="-0.375 -0.375 18 18"
                  height="18"
                  width="18"
                  id="Arrow-Right-1--Streamline-Ultimate"
                  className="stroke-current"
                >
                  <desc>
                    Arrow Right 1 Streamline Icon: https://streamlinehq.com
                  </desc>
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m4.0616562499999995 16.890625 9.039 -7.9968125a0.359375 0.359375 0 0 0 0 -0.537625L4.0616562499999995 0.359375"
                    strokeWidth="0.75"
                  ></path>
                </svg>
              </span>
            </li>
          ))}
      </ul>

      <a
        href={props.cta?.link}
        target="_blank"
        rel="noreferrer"
        className="self-start"
        data-track="immodossier valiant blog cta link"
      >
        <Button
          primary={props.cta?.isPrimary}
          showArrow
        >
          {t(props.cta?.buttonText || 'widget_blog_action_button_text')}
        </Button>
      </a>
    </div>
  );
}
